import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Mail, RotateCcw, Eye, AlertCircle, Save, Loader2, CheckCircle2, Send } from "lucide-react";
import { cn } from "@/lib/utils";
import { listEmailTemplates, sendTemplatedEmail, updateEmailTemplate } from "@/services/emailApi";
import type { EmailTemplateDTO } from "@/types/api";
import { useEmailTemplateOverrides } from "@/hooks/useEmailTemplateOverrides";
import { getCurrentUserEmail } from "@/lib/auth";

/**
 * Feature flag — set VITE_TEMPLATES_API_ENABLED=true once the backend
 * exposes GET / PUT /api/v1/emails/templates[/:type]. Until then, this
 * page stays in read-only mock mode (session-only overrides, no save).
 */
const TEMPLATES_API_ENABLED =
  import.meta.env.VITE_TEMPLATES_API_ENABLED === "true";

/**
 * Hardcoded catalog of common templated email types the backend
 * `EmailController` (POST /api/v1/emails/send) currently supports.
 *
 * The backend does NOT yet expose a templates CRUD endpoint, so this
 * page is a read-only catalog with a client-side mock preview. Once
 * GET/PUT /api/v1/emails/templates ships, the `defaultSubject` /
 * `defaultBody` here can be replaced with server-fetched values, and
 * the local override state can be promoted to a real save call.
 */
interface TemplateDefinition {
  type: string;
  displayName: string;
  description: string;
  defaultSubject: string;
  defaultBody: string;
  variables: { name: string; sample: string; description: string }[];
}

const TEMPLATE_CATALOG: TemplateDefinition[] = [
  {
    type: "INVITE_USER",
    displayName: "Invite user to enroll",
    description:
      "Sent when an admin invites an end user to enroll a new device from the Devices → Enroll Device dialog.",
    defaultSubject: "You're invited to enroll your device with {{org_name}}",
    defaultBody:
      "Hi,\n\n{{org_name}} has invited you to enroll your device with smplify.\n\nClick the link below to start enrollment:\n{{enrol_link}}\n\nThis link is unique to you — please don't share it.",
    variables: [
      { name: "enrol_link", sample: "https://console.smplify.com/enrol/device", description: "Public enrollment URL" },
      { name: "invite_link", sample: "https://console.smplify.com/enrol/device", description: "Alias for enrol_link" },
      { name: "org_name", sample: "Acme Corp", description: "Tenant display name" },
    ],
  },
  {
    type: "RESET_PASSWORD",
    displayName: "Reset password",
    description:
      "Sent when a console user requests a password reset from the login page.",
    defaultSubject: "Reset your smplify password",
    defaultBody:
      "Hi,\n\nWe received a request to reset your smplify password.\n\nClick the link below to set a new one:\n{{reset_link}}\n\nIf you didn't request this, you can safely ignore this email.",
    variables: [
      { name: "reset_link", sample: "https://console.smplify.com/reset/abc123", description: "One-time password reset URL" },
    ],
  },
  {
    type: "WELCOME",
    displayName: "Welcome to smplify",
    description:
      "Sent to new console users after their account is created and confirmed.",
    defaultSubject: "Welcome to {{org_name}} on smplify",
    defaultBody:
      "Hi {{user_name}},\n\nYour smplify account is ready. You can sign in any time at:\n{{console_url}}\n\nIf you need help getting started, check out our docs.",
    variables: [
      { name: "user_name", sample: "Jordan", description: "Recipient's first name" },
      { name: "org_name", sample: "Acme Corp", description: "Tenant display name" },
      { name: "console_url", sample: "https://console.smplify.com", description: "Console login URL" },
    ],
  },
  {
    type: "DEVICE_ENROLLED",
    displayName: "Device enrolled",
    description:
      "Notification sent to admins when a new device successfully completes enrollment.",
    defaultSubject: "New device enrolled: {{device_name}}",
    defaultBody:
      "A new device has been enrolled in {{org_name}}.\n\nDevice: {{device_name}}\nSerial: {{serial_number}}\nUser: {{user_email}}\n\nView in console: {{device_url}}",
    variables: [
      { name: "device_name", sample: "Jordan's MacBook Pro", description: "Device display name" },
      { name: "serial_number", sample: "C02XXXXXXXXX", description: "Device serial number" },
      { name: "user_email", sample: "jordan@acme.com", description: "Assigned user's email" },
      { name: "device_url", sample: "https://console.smplify.com/devices/abc", description: "Device detail URL" },
      { name: "org_name", sample: "Acme Corp", description: "Tenant display name" },
    ],
  },
  {
    type: "DEVICE_UNENROLLED",
    displayName: "Device unenrolled",
    description:
      "Notification sent when a device is removed from management (admin or user-initiated).",
    defaultSubject: "Device unenrolled: {{device_name}}",
    defaultBody:
      "The following device has been unenrolled from {{org_name}}:\n\nDevice: {{device_name}}\nSerial: {{serial_number}}\nUnenrolled at: {{unenrolled_at}}\n\nIf this wasn't expected, contact your administrator.",
    variables: [
      { name: "device_name", sample: "Jordan's MacBook Pro", description: "Device display name" },
      { name: "serial_number", sample: "C02XXXXXXXXX", description: "Device serial number" },
      { name: "unenrolled_at", sample: "2026-04-18 14:32 UTC", description: "Unenrolment timestamp" },
      { name: "org_name", sample: "Acme Corp", description: "Tenant display name" },
    ],
  },
  {
    type: "COMPLIANCE_ALERT",
    displayName: "Compliance alert",
    description:
      "Sent to admins when a device fails a compliance rule (e.g. OS out of date, encryption disabled).",
    defaultSubject: "Compliance alert: {{rule_name}} on {{device_name}}",
    defaultBody:
      "A compliance issue was detected:\n\nDevice: {{device_name}}\nRule: {{rule_name}}\nSeverity: {{severity}}\n\nReview the device: {{device_url}}",
    variables: [
      { name: "device_name", sample: "Jordan's MacBook Pro", description: "Affected device" },
      { name: "rule_name", sample: "macOS minimum version", description: "Compliance rule that failed" },
      { name: "severity", sample: "High", description: "Alert severity (Low / Medium / High / Critical)" },
      { name: "device_url", sample: "https://console.smplify.com/devices/abc", description: "Device detail URL" },
    ],
  },
];

interface OverrideState {
  subject: string;
  body: string;
}

/** Replace {{var}} placeholders with their sample values for preview. */
function renderWithVariables(text: string, variables: TemplateDefinition["variables"]): string {
  return variables.reduce(
    (acc, v) => acc.split(`{{${v.name}}}`).join(v.sample),
    text
  );
}

export function EmailTemplatesSettings() {
  const queryClient = useQueryClient();
  const [selectedType, setSelectedType] = useState<string>(TEMPLATE_CATALOG[0].type);

  // localStorage-backed source of truth for trigger sites (e.g. EnrollDeviceDialog).
  // We always mirror saved overrides here so the dialog can read them
  // synchronously without a network round-trip, regardless of whether the
  // backend templates API is enabled.
  const {
    overrides: storedOverrides,
    setOverride: persistOverride,
    clearOverride: clearStoredOverride,
    replaceAll: replaceAllStored,
  } = useEmailTemplateOverrides();

  // Local edit buffer — keyed by template_type. Hydrated from localStorage,
  // and (when the API is enabled) refreshed from the server on first load.
  const [overrides, setOverrides] = useState<Record<string, OverrideState>>(() => {
    const init: Record<string, OverrideState> = {};
    for (const def of TEMPLATE_CATALOG) {
      const stored = storedOverrides[def.type];
      if (stored && (stored.custom_subject || stored.custom_body)) {
        init[def.type] = {
          subject: stored.custom_subject ?? def.defaultSubject,
          body: stored.custom_body ?? def.defaultBody,
        };
      }
    }
    return init;
  });
  // Tracks the last server-confirmed values so we can detect "dirty" edits
  // and a successful save can roll the baseline forward.
  const [serverBaseline, setServerBaseline] = useState<Record<string, OverrideState>>({});

  const templatesQuery = useQuery({
    queryKey: ["email-templates"],
    queryFn: listEmailTemplates,
    enabled: TEMPLATES_API_ENABLED,
    staleTime: 5 * 60 * 1000,
  });

  // Hydrate local overrides + baseline once the server data lands, and
  // mirror the server response into localStorage so trigger sites pick up
  // the latest tenant-scoped values without a separate fetch.
  useEffect(() => {
    if (!TEMPLATES_API_ENABLED) return;
    const data = templatesQuery.data;
    if (!data || !Array.isArray(data)) return;
    const next: Record<string, OverrideState> = {};
    const mirror: Record<string, { custom_subject?: string; custom_body?: string }> = {};
    for (const t of data as EmailTemplateDTO[]) {
      const def = TEMPLATE_CATALOG.find((c) => c.type === t.template_type);
      if (!def) continue;
      const subject = t.custom_subject ?? def.defaultSubject;
      const body = t.custom_body ?? def.defaultBody;
      if (subject !== def.defaultSubject || body !== def.defaultBody) {
        next[t.template_type] = { subject, body };
        mirror[t.template_type] = {
          custom_subject: subject !== def.defaultSubject ? subject : undefined,
          custom_body: body !== def.defaultBody ? body : undefined,
        };
      }
    }
    setServerBaseline(next);
    setOverrides(next);
    replaceAllStored(mirror);
  }, [templatesQuery.data, replaceAllStored]);

  const saveMutation = useMutation({
    mutationFn: async (vars: { type: string; subject: string; body: string; defaults: { subject: string; body: string } }) => {
      const isDefault =
        vars.subject === vars.defaults.subject && vars.body === vars.defaults.body;
      // Sending undefined clears the override server-side; otherwise persist the custom value.
      return updateEmailTemplate(vars.type, {
        custom_subject: isDefault || vars.subject === vars.defaults.subject ? undefined : vars.subject,
        custom_body: isDefault || vars.body === vars.defaults.body ? undefined : vars.body,
      });
    },
    onSuccess: (_data, vars) => {
      // Roll the baseline forward so the dirty indicator clears.
      setServerBaseline((prev) => {
        const next = { ...prev };
        if (vars.subject === vars.defaults.subject && vars.body === vars.defaults.body) {
          delete next[vars.type];
        } else {
          next[vars.type] = { subject: vars.subject, body: vars.body };
        }
        return next;
      });
      // Mirror to localStorage so EnrollDeviceDialog & other triggers
      // pick up the saved override on the next send.
      if (vars.subject === vars.defaults.subject && vars.body === vars.defaults.body) {
        clearStoredOverride(vars.type);
      } else {
        persistOverride(vars.type, {
          custom_subject: vars.subject !== vars.defaults.subject ? vars.subject : undefined,
          custom_body: vars.body !== vars.defaults.body ? vars.body : undefined,
        });
      }
      queryClient.invalidateQueries({ queryKey: ["email-templates"] });
      toast.success("Template override saved");
    },
    onError: (err: Error) => {
      toast.error(err?.message || "Failed to save template override");
    },
  });

  // Send a real test email to the current admin so they can preview the
  // rendered template (with their in-progress overrides applied) in their
  // own inbox. Uses the same /api/v1/emails/send endpoint that production
  // trigger sites use, so what they see is exactly what end users will get.
  const testSendMutation = useMutation({
    mutationFn: async (vars: {
      type: string;
      subject: string;
      body: string;
      defaults: { subject: string; body: string };
      variables: { name: string; sample: string }[];
      recipient: string;
    }) => {
      // Build a sample variables map so backend substitution renders with
      // realistic values (matches the on-screen Preview pane).
      const sampleVars: Record<string, string> = {};
      for (const v of vars.variables) sampleVars[v.name] = v.sample;
      return sendTemplatedEmail({
        recipients: [vars.recipient],
        template_type: vars.type,
        variables: sampleVars,
        // Only forward the override fields the admin actually customised —
        // otherwise the backend falls back to its default template copy.
        ...(vars.subject !== vars.defaults.subject
          ? { custom_subject: vars.subject }
          : {}),
        ...(vars.body !== vars.defaults.body
          ? { custom_body: vars.body }
          : {}),
      });
    },
    onSuccess: (_data, vars) => {
      toast.success(`Test email sent to ${vars.recipient}`);
    },
    onError: (err: Error) => {
      toast.error(err?.message || "Failed to send test email");
    },
  });

  const selected = useMemo(
    () => TEMPLATE_CATALOG.find((t) => t.type === selectedType) ?? TEMPLATE_CATALOG[0],
    [selectedType]
  );

  const currentOverride = overrides[selected.type] ?? {
    subject: selected.defaultSubject,
    body: selected.defaultBody,
  };

  const isOverridden =
    currentOverride.subject !== selected.defaultSubject ||
    currentOverride.body !== selected.defaultBody;

  // Dirty = local edits differ from what the server (or the default, if no
  // server override exists) currently has. Only meaningful when API is enabled.
  const baseline = serverBaseline[selected.type] ?? {
    subject: selected.defaultSubject,
    body: selected.defaultBody,
  };
  const isDirty =
    TEMPLATES_API_ENABLED &&
    (currentOverride.subject !== baseline.subject ||
      currentOverride.body !== baseline.body);

  const updateOverride = (patch: Partial<OverrideState>) => {
    const nextValue = { ...currentOverride, ...patch };
    setOverrides((prev) => ({ ...prev, [selected.type]: nextValue }));
    // When the backend API is OFF, autosave to localStorage so trigger
    // sites (EnrollDeviceDialog) immediately reflect the admin's edits.
    // When the API is ON, defer persistence until the user clicks Save.
    if (!TEMPLATES_API_ENABLED) {
      const isDefaultNow =
        nextValue.subject === selected.defaultSubject &&
        nextValue.body === selected.defaultBody;
      if (isDefaultNow) {
        clearStoredOverride(selected.type);
      } else {
        persistOverride(selected.type, {
          custom_subject:
            nextValue.subject !== selected.defaultSubject ? nextValue.subject : undefined,
          custom_body:
            nextValue.body !== selected.defaultBody ? nextValue.body : undefined,
        });
      }
    }
  };

  const resetToDefault = () => {
    setOverrides((prev) => {
      const next = { ...prev };
      delete next[selected.type];
      return next;
    });
    if (!TEMPLATES_API_ENABLED) {
      clearStoredOverride(selected.type);
    }
  };

  const handleSave = () => {
    saveMutation.mutate({
      type: selected.type,
      subject: currentOverride.subject,
      body: currentOverride.body,
      defaults: { subject: selected.defaultSubject, body: selected.defaultBody },
    });
  };

  const handleTestSend = () => {
    const recipient = getCurrentUserEmail();
    if (!recipient) {
      toast.error("Could not determine your admin email — please re-login and try again.");
      return;
    }
    testSendMutation.mutate({
      type: selected.type,
      subject: currentOverride.subject,
      body: currentOverride.body,
      defaults: { subject: selected.defaultSubject, body: selected.defaultBody },
      variables: selected.variables,
      recipient,
    });
  };

  const previewSubject = renderWithVariables(currentOverride.subject, selected.variables);
  const previewBody = renderWithVariables(currentOverride.body, selected.variables);


  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Email Templates
          </CardTitle>
          <CardDescription>
            Review the templated emails sent via the EmailController
            (<code className="text-xs">POST /api/v1/emails/send</code>) and
            preview how they look. Per-tenant overrides for{" "}
            <code className="text-xs">custom_subject</code> and{" "}
            <code className="text-xs">custom_body</code> can be edited here
            and will be wired to the backend once a templates CRUD endpoint
            is available.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {TEMPLATES_API_ENABLED ? (
            templatesQuery.isError ? (
              <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm">
                <AlertCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
                <div className="space-y-1">
                  <p className="font-medium">Failed to load templates</p>
                  <p className="text-muted-foreground">
                    {(templatesQuery.error as Error)?.message ||
                      "Could not fetch tenant overrides from the server."}
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2 rounded-md border border-success/40 bg-success/10 p-3 text-sm">
                <CheckCircle2 className="h-4 w-4 text-success mt-0.5 flex-shrink-0" />
                <div className="space-y-1">
                  <p className="font-medium">Persistence enabled</p>
                  <p className="text-muted-foreground">
                    Overrides are loaded from and saved to{" "}
                    <code>/api/v1/emails/templates</code> per tenant.
                  </p>
                </div>
              </div>
            )
          ) : (
            <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 p-3 text-sm">
              <AlertCircle className="h-4 w-4 text-warning mt-0.5 flex-shrink-0" />
              <div className="space-y-1">
                <p className="font-medium">Local-only persistence</p>
                <p className="text-muted-foreground">
                  Overrides are saved to this browser (per-tenant{" "}
                  <code>localStorage</code>) and used by trigger sites such
                  as the Enrol Device email invite. They will sync server-side
                  once <code>VITE_TEMPLATES_API_ENABLED=true</code> and the
                  backend exposes <code>GET / PUT /api/v1/emails/templates</code>.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        {/* Template list */}
        <Card className="h-fit">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Templates</CardTitle>
          </CardHeader>
          <CardContent className="p-2">
            <nav className="flex flex-col gap-1" aria-label="Email template types">
              {TEMPLATE_CATALOG.map((t) => {
                const overridden = !!overrides[t.type];
                const active = t.type === selectedType;
                return (
                  <button
                    key={t.type}
                    type="button"
                    onClick={() => setSelectedType(t.type)}
                    className={cn(
                      "w-full text-left rounded-md px-3 py-2 text-sm transition-colors",
                      "hover:bg-accent hover:text-accent-foreground",
                      active && "bg-accent text-accent-foreground"
                    )}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium truncate">{t.displayName}</span>
                      {overridden && (
                        <Badge variant="secondary" className="text-[10px]">
                          edited
                        </Badge>
                      )}
                    </div>
                    <code className="text-[11px] text-muted-foreground">{t.type}</code>
                  </button>
                );
              })}
            </nav>
          </CardContent>
        </Card>

        {/* Editor + preview */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <CardTitle>{selected.displayName}</CardTitle>
                  <CardDescription className="mt-1">{selected.description}</CardDescription>
                </div>
                <Badge variant="outline" className="font-mono text-xs">
                  {selected.type}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email-subject">Custom subject</Label>
                <Input
                  id="email-subject"
                  value={currentOverride.subject}
                  onChange={(e) => updateOverride({ subject: e.target.value })}
                  placeholder={selected.defaultSubject}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email-body">Custom body</Label>
                <Textarea
                  id="email-body"
                  value={currentOverride.body}
                  onChange={(e) => updateOverride({ body: e.target.value })}
                  placeholder={selected.defaultBody}
                  rows={10}
                  className="font-mono text-sm"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Available variables
                </Label>
                <div className="flex flex-wrap gap-2">
                  {selected.variables.map((v) => (
                    <Badge
                      key={v.name}
                      variant="outline"
                      className="font-mono text-xs"
                      title={v.description}
                    >
                      {`{{${v.name}}}`}
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-between pt-2 gap-3 flex-wrap">
                <p className="text-xs text-muted-foreground">
                  {isOverridden
                    ? "Override active — preview reflects your edits."
                    : "Showing default template content."}
                  {TEMPLATES_API_ENABLED && isDirty && (
                    <span className="ml-2 text-warning">• Unsaved changes</span>
                  )}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleTestSend}
                    disabled={testSendMutation.isPending}
                    title="Send the current template (with your edits) to your own admin email"
                  >
                    {testSendMutation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    Send test email
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={resetToDefault}
                    disabled={!isOverridden || saveMutation.isPending}
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Reset to default
                  </Button>
                  {TEMPLATES_API_ENABLED && (
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleSave}
                      disabled={!isDirty || saveMutation.isPending || templatesQuery.isLoading}
                    >
                      {saveMutation.isPending ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4 mr-2" />
                      )}
                      Save changes
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Mock preview */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Eye className="h-4 w-4" />
                Preview
              </CardTitle>
              <CardDescription>
                Variables substituted with sample values. The real template
                rendering on the backend may differ.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border border-border bg-card overflow-hidden">
                <div className="border-b border-border bg-muted/40 px-4 py-3 space-y-1">
                  <div className="flex items-baseline gap-2 text-xs text-muted-foreground">
                    <span className="font-medium">From:</span>
                    <span>noreply@smplify.com</span>
                  </div>
                  <div className="flex items-baseline gap-2 text-xs text-muted-foreground">
                    <span className="font-medium">To:</span>
                    <span>recipient@example.com</span>
                  </div>
                  <Separator className="my-2" />
                  <div className="text-sm font-semibold text-foreground">
                    {previewSubject || (
                      <span className="text-muted-foreground italic">(empty subject)</span>
                    )}
                  </div>
                </div>
                <div className="px-4 py-5 bg-background">
                  <pre className="whitespace-pre-wrap font-sans text-sm text-foreground leading-relaxed">
                    {previewBody || (
                      <span className="text-muted-foreground italic">(empty body)</span>
                    )}
                  </pre>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
